import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, OutlinedInput, Typography } from '@mui/material';
import { AssetDropdownSelect } from 'components';
import { useWeb3React } from '@web3-react/core';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { DEPOSIT_CONTRACT } from 'data/depositContract';
import { getDepositContract } from 'utils/depositContractHelpers';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { toast } from 'react-toastify';
import { getErc20Contract } from 'utils/contractHelpers';
import { userDeposit } from 'apis';

const inputStyles = {
	'&, &:hover': {
		outline: '1px solid #FBFBFB',
		//outline: '1px solid #235EE1',
	},
	color: 'white',
	fontWeight: 500,
	'& .MuiOutlinedInput-root': {
		fontWeight: 500,
	},
	'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
		display: 'none',
	},
};

const DepositPanel: React.ComponentType<{ onClose?(): void }> = ({
	onClose,
}) => {
	const [depositAmount, setDepositAmount] = useState('');
	const [allowance, setAllowance] = useState('0');
	const [loading, setLoading] = useState(true);
	const [balance, setBalance] = useState('0');
	const { account, library } = useWeb3React();
	const appState = useSelector((state: RootState) => state.app);
	const assetAddress =
		DEPOSIT_CONTRACT[appState.selectedNetwork].token.find(
			(d) => d.name === 'USDC',
		)?.address || '';
	const contractAddress =
		DEPOSIT_CONTRACT[appState.selectedNetwork].contractAddress;

	const fetchAllowance = useCallback(async () => {
		try {
			const assetContract = getErc20Contract(
				assetAddress,
				library.getSigner(),
			);
			const assetAllowance = await assetContract.allowance(
				account,
				contractAddress,
			);
			setAllowance(formatUnits(assetAllowance, 6) || '0');
		} catch (e) {
			//
		}
	}, [account, assetAddress, contractAddress, library]);
	const fetchBalance = useCallback(async () => {
		try {
			const assetContract = getErc20Contract(
				assetAddress,
				library.getSigner(),
			);
			const assetBalance = await assetContract.balanceOf(account);
			setBalance(formatUnits(assetBalance, 6));
		} catch (e) {
			//
		} finally {
			setLoading(false);
		}
	}, [account, assetAddress, library]);

	const fillMaxAmount = useCallback(() => {
		setDepositAmount(balance.toString());
	}, [balance]);

	const endAdornment = useMemo(() => {
		return (
			<Box>
				<Button onClick={fillMaxAmount}>
					<Typography fontSize={12} fontWeight={500}>
						MAX
					</Typography>
				</Button>
			</Box>
		);
	}, [fillMaxAmount]);

	useEffect(() => {
		setDepositAmount('');
	}, []);

	const deposit = async () => {
		try {
			setLoading(true);
			const contract = getDepositContract(
				contractAddress,
				library,
				account as string,
			);

			if (parseFloat(allowance) < parseFloat(depositAmount)) {
				const assetContract = getErc20Contract(
					assetAddress,
					library.getSigner(),
				);
				const tx = await assetContract.approve(
					contractAddress,
					parseUnits(depositAmount.toString(), 6),
				);
				await assetContract.provider.waitForTransaction(tx.hash);
				const updatedAllowance = await assetContract.allowance(
					account,
					contractAddress,
				);

				if (updatedAllowance < depositAmount) {
					toast('Please allow USDC spending', {
						type: 'error',
					});
					return;
				}
			}
			const dpt = await contract.deposit(
				parseUnits(depositAmount, 6),
				account,
			);
			await dpt.wait();
			await userDeposit({
				contract: contractAddress,
				address: account as string,
				amount: depositAmount,
			});
			onClose?.();
			toast('Successfully staked into vault contract', {
				type: 'success',
			});
		} catch (e) {
			toast('Something went wrong', { type: 'error' });
		} finally {
			fetchBalance();
			setLoading(false);
		}
	};

	const buttonLabel = useMemo(() => {
		if (!account) return 'Connect Wallet';

		if (loading) return 'Please wait...';

		return 'Deposit';
	}, [account, loading]);

	useEffect(() => {
		fetchAllowance();
		fetchBalance();
	}, [fetchAllowance, fetchBalance]);

	return (
		<Box>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
			>
				<Typography color="#E6E6E9">Select asset</Typography>
				<Box display="flex" alignItems="center">
					<Typography color="#E6E6E9" fontWeight={500} fontSize={12}>
						{balance} {'USDC'}
					</Typography>
					<Box width={4} />
					<img src="/icons/wallet.png" />
				</Box>
			</Box>
			<Box height={8} />
			<AssetDropdownSelect />

			<Box height={16} />

			<Typography color="#E6E6E9">Amount</Typography>
			<Box height={8} />
			<OutlinedInput
				disabled={loading}
				placeholder="0.00"
				value={depositAmount}
				onChange={(event) => {
					setDepositAmount(event.target.value);
				}}
				type="number"
				fullWidth
				margin="dense"
				sx={inputStyles}
				endAdornment={endAdornment}
			/>

			<Box height={28} />

			<Button
				onClick={deposit}
				disabled={
					balance < depositAmount || loading || depositAmount === ''
				}
				sx={{
					height: 52,
				}}
				variant="contained"
				fullWidth
				color="primary"
			>
				<Typography
					variant={'button'}
					fontWeight="bold"
					py="6px"
					style={{ lineHeight: '120%' }}
				>
					{buttonLabel}
				</Typography>
			</Button>
		</Box>
	);
};

export { DepositPanel };
